import CheckIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import React, { useMemo, useState } from 'react';
import { PlanogramComplianceConfigItem } from '../../../types';

interface PlanogramComplianceAdvancedSettingsProps {
  config: PlanogramComplianceConfigItem[];
  defaultConfig?: PlanogramComplianceConfigItem[] | null;
  onConfigChange: (key: string, value: number | boolean | string) => void;
  onReset?: () => void;
  children?: React.ReactNode;
  isV2: boolean;
}

const TYPE_ORDER: Record<string, number> = { number: 0, boolean: 1, enum: 2 };

export const PlanogramComplianceAdvancedSettings: React.FC<
  PlanogramComplianceAdvancedSettingsProps
> = ({ config, defaultConfig, onConfigChange, onReset, children, isV2 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isModified = useMemo(() => {
    if (!defaultConfig) return false;
    return config.some((item) => {
      const defaultItem = defaultConfig.find((d) => d.key === item.key);
      return defaultItem && item.value !== defaultItem.value;
    });
  }, [config, defaultConfig]);

  const sortedConfig = useMemo(
    () => [...config].sort((a, b) => (TYPE_ORDER[a.type] ?? 3) - (TYPE_ORDER[b.type] ?? 3)),
    [config]
  );

  const numberItems = sortedConfig.filter((item) => item.type === 'number');
  const booleanItems = sortedConfig.filter((item) => item.type === 'boolean');
  const enumItems = sortedConfig.filter((item) => item.type === 'enum');

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Advanced Settings</span>
          {isModified && (
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Modified</span>
          )}
        </div>
        {isExpanded ? (
          <ExpandLessIcon className="text-gray-600" />
        ) : (
          <ExpandMoreIcon className="text-gray-600" />
        )}
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="p-4 space-y-6 bg-white">
          {children}
          <div className={`space-y-6 ${isV2 ? 'hidden' : ''}`}>
            {/* Number Inputs */}
            {numberItems.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {numberItems.map((item) => (
                  <div key={item.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {item.label}
                    </label>
                    <input
                      type="number"
                      value={(item.value as number) ?? ''}
                      onChange={(e) => {
                        const trimmed = e.target.value.trim();
                        const parsed = parseFloat(trimmed);
                        const numValue =
                          trimmed === '' || !Number.isFinite(parsed)
                            ? (item.defaultValue as number)
                            : parsed;
                        onConfigChange(item.key, numValue);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={String(item.defaultValue)}
                      step={Number(item.defaultValue) % 1 !== 0 ? '0.01' : '1'}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Boolean Toggles */}
            {booleanItems.length > 0 && (
              <div className="flex flex-wrap items-center gap-4">
                {booleanItems.map((item) => (
                  <div key={item.key} className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{item.label}:</span>
                    <button
                      onClick={() => onConfigChange(item.key, !item.value)}
                      className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                        item.value
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white border-gray-300 hover:border-gray-400'
                      }`}
                      title={`Toggle ${item.label}`}
                    >
                      {item.value && <CheckIcon sx={{ fontSize: 16 }} />}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Enum Selects */}
            {enumItems.length > 0 && (
              <div className="flex flex-wrap items-center gap-4">
                {enumItems.map((item) => (
                  <div key={item.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {item.label}
                    </label>
                    <select
                      value={String(item.value)}
                      onChange={(e) => onConfigChange(item.key, e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {item.options?.map((option) => (
                        <option key={option} value={option}>
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}

            {onReset && (
              <div className="flex justify-end">
                <button
                  onClick={onReset}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <RestartAltIcon fontSize="small" />
                  Reset to Defaults
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
